import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  CircularProgress,
  Container,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  SportsEsports as GameIcon,
  Person as PersonIcon,
  Info as InfoIcon,
  CheckCircle as ApprovedIcon,
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  Block as BlockIcon,
  WatchLater as WatchLaterIcon
} from '@mui/icons-material';
import api from '../services/api';
import { AuthContext } from '../context/AuthContext';
import MatchCard from '../components/MatchCard';
import PendingMatchCard from '../components/PendingMatchCard';
import CancelMatchCard from '../components/CancelMatchCard';
import RecentMatchesSection from '../components/RecentMatchesSection';
import PageTitle from '../components/PageTitle';
import SectionTitle from '../components/SectionTitle';

function MyMatchesPage() {
  const [matches, setMatches] = useState([]);
  const [pendingMatches, setPendingMatches] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [isLoadingMatches, setIsLoadingMatches] = useState(true);
  const [isLoadingPendingMatches, setIsLoadingPendingMatches] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(() => {
    const savedTab = localStorage.getItem('selectedTab');
    const locationState = window.history.state?.selectedTab;
    const tabValue = locationState !== undefined ? locationState : (savedTab ? parseInt(savedTab) : 0);
    return tabValue > 1 ? 0 : tabValue;
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTerm, setFilterTerm] = useState('');
  const [sortTerm, setSortTerm] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Filtrar partidas que necesitan aprobación del usuario
  const matchesNeedingUserApproval = pendingMatches.filter(match => {
    try {
      const userId = String(user.id);
      
      // Buscar al usuario en los equipos
      const team1Player = match.team1?.players?.find(p => String(p.user_id) === userId);
      const team2Player = match.team2?.players?.find(p => String(p.user_id) === userId);
      
      // Si el usuario está en un equipo y su estado es 'pending', necesita aprobar
      return (team1Player?.approval_status === 'pending' || team2Player?.approval_status === 'pending');
    } catch (error) {
      console.error('Error checking approval status:', error);
      return false;
    }
  });
  
  // Filtrar partidas pendientes que NO necesitan aprobación del usuario
  const otherPendingMatches = pendingMatches.filter(match => {
    try {
      const userId = String(user.id);
      
      // Buscar al usuario en los equipos
      const team1Player = match.team1?.players?.find(p => String(p.user_id) === userId);
      const team2Player = match.team2?.players?.find(p => String(p.user_id) === userId);
      
      // Si el usuario no está en ningún equipo, no mostrar la partida
      if (!team1Player && !team2Player) {
        return false;
      }

      // Verificar si el usuario ya aprobó su participación
      const userHasApproved = (team1Player?.approval_status === 'approved' || 
                              team2Player?.approval_status === 'approved');
      
      // Si el usuario no ha aprobado, no mostrar en esta sección
      if (!userHasApproved) {
        return false;
      }

      // Verificar si hay otros jugadores que aún no han aprobado
      const otherPlayers = [...(match.team1?.players || []), ...(match.team2?.players || [])]
        .filter(p => String(p.user_id) !== userId);
      
      return otherPlayers.some(p => p.approval_status === 'pending');
    } catch (error) {
      console.error('Error filtering match:', error);
      return false;
    }
  });

  // Filtrar partidas aprobadas para la tab "Mis Recientes"
  const approvedMatches = matches.filter(match => {
    try {
      const isFullyApproved = match.team1?.players?.every(p => p.approval_status === 'approved') &&
                            match.team2?.players?.every(p => p.approval_status === 'approved');
      return isFullyApproved;
    } catch (error) {
      console.error('Error filtering approved matches:', error);
      return false;
    }
  });

  // Agregar después de las otras funciones de filtrado
  const cancelledMatches = matches.filter(match => {
    try {
      const isCancelled = match.approval_status === 'cancelled';
      return isCancelled;
    } catch (error) {
      console.error('Error filtering cancelled matches:', error);
      return false;
    }
  });

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    localStorage.setItem('selectedTab', newValue);
  };

  const fetchData = async () => {
    try {
      const [matchesRes, pendingMatchesRes] = await Promise.all([
        api.get('/api/matches/me'),
        api.get('/api/matches/pending')
      ]);
      
      // Filtrar partidas pendientes que realmente necesitan aprobación
      const validPendingMatches = pendingMatchesRes.data.filter(match => {
        try {
          const userId = String(user.id);
          
          // Exclude matches that are already cancelled
          if (match.approval_status === 'cancelled') {
            return false;
          }

          // Buscar al usuario en los equipos
          const team1Player = match.team1?.players?.find(p => String(p.user_id) === userId);
          const team2Player = match.team2?.players?.find(p => String(p.user_id) === userId);
          
          // Solo incluir partidas donde el usuario está en alguno de los equipos
          if (!team1Player && !team2Player) {
            return false;
          }

          // Si ambos equipos ya aprobaron, no es una partida pendiente
          const isFullyApproved = match.team1?.players?.every(p => p.approval_status === 'approved') &&
                                match.team2?.players?.every(p => p.approval_status === 'approved');
          
          return !isFullyApproved;
        } catch (error) {
          console.error('Error parsing match data:', error);
          return false;
        }
      });
      
      // Ordenar partidas pendientes
      const sortedPendingMatches = validPendingMatches.sort((a, b) => {
        return new Date(b.match_date) - new Date(a.match_date);
      });

      setPendingMatches(sortedPendingMatches);
      setMatches(matchesRes.data);
      setError(null);
    } catch (error) {
      console.error('Error en fetchData:', error);
      setError('Error al cargar los datos');
    } finally {
      setIsLoadingMatches(false);
      setIsLoadingPendingMatches(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/api/users');
      const map = {};
      res.data.forEach((u) => {
        map[u.id] = u.username;
      });
      setUsersMap(map);
    } catch (error) {
      console.error(error);
    }
  };

  const handleApprove = async (matchId) => {
    try {
      await api.post(`/api/matches/${matchId}/approve`);
      
      // Actualizar la lista de partidas pendientes
      setPendingMatches(prevMatches => 
        prevMatches.filter(match => match.id !== matchId)
      );
      
      // Recargar las partidas aprobadas
      const matchesRes = await api.get('/api/matches/me');
      const approvedMatches = matchesRes.data.filter(match => {
        try {
          const isFullyApproved = match.team1?.players?.every(p => p.approval_status === 'approved') &&
                                match.team2?.players?.every(p => p.approval_status === 'approved');
          return isFullyApproved;
        } catch (error) {
          console.error('Error parsing match data:', error);
          return false;
        }
      });
      setMatches(approvedMatches);
      
      // Mostrar mensaje de éxito
      handleSnackbarOpen('Partida aprobada correctamente');
    } catch (error) {
      console.error('Error al aprobar la partida:', error);
      handleSnackbarOpen('Error al aprobar la partida', 'error');
    }
  };

  const handleReject = async (matchId) => {
    try {
      await api.post(`/api/matches/${matchId}/reject`);
      
      // Actualizar la lista de partidas pendientes
      setPendingMatches(prevMatches => 
        prevMatches.filter(match => match.id !== matchId)
      );
      
      // Mostrar mensaje de éxito
      handleSnackbarOpen('Partida rechazada correctamente');
    } catch (error) {
      console.error('Error al rechazar la partida:', error);
      handleSnackbarOpen('Error al rechazar la partida', 'error');
    }
  };

  const handleDelete = async (matchId) => {
    try {
      await api.delete(`/api/matches/${matchId}`);
      
      // Actualizar la lista de partidas
      setMatches(prevMatches => 
        prevMatches.filter(match => match.id !== matchId)
      );
      
      // Mostrar mensaje de éxito
      handleSnackbarOpen('Partida eliminada correctamente');
    } catch (error) {
      console.error('Error al eliminar la partida:', error);
      handleSnackbarOpen('Error al eliminar la partida', 'error');
    }
  };

  const handleEdit = (matchId) => {
    navigate(`/matches/${matchId}/edit`);
  };

  const handleView = (matchId) => {
    navigate(`/matches/${matchId}`);
  };

  const handleSnackbarClose = () => {
    setSnackbar({
      open: false,
      message: '',
      severity: 'success'
    });
  };

  const handleSnackbarOpen = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCreateMatch = () => {
    navigate('/matches/new');
  };

  const handleRefresh = async () => {
    setIsLoadingMatches(true);
    setIsLoadingPendingMatches(true);
    try {
      await fetchData();
      handleSnackbarOpen('Datos actualizados correctamente');
    } catch (error) {
      console.error('Error al actualizar los datos:', error);
      handleSnackbarOpen('Error al actualizar los datos', 'error');
    } finally {
      setIsLoadingMatches(false);
      setIsLoadingPendingMatches(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleProfile = () => {
    navigate('/profile');
  };

  const handleSettings = () => {
    navigate('/settings');
  };

  const handleHelp = () => {
    navigate('/help');
  };

  const handleContact = () => {
    navigate('/contact');
  };

  const handleTerms = () => {
    navigate('/terms');
  };

  const handlePrivacy = () => {
    navigate('/privacy');
  };

  const handleCookies = () => {
    navigate('/cookies');
  };

  const handleSitemap = () => {
    navigate('/sitemap');
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilter = (event) => {
    setFilterTerm(event.target.value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  useEffect(() => {
    console.log('DEBUG PENDIENTES OTROS - Estado actual:', {
      totalPendingMatches: pendingMatches.length,
      matchesNeedingApproval: matchesNeedingUserApproval.length,
      otherPendingMatches: otherPendingMatches.length,
      activeTab: tab,
      currentUserId: user?.id,
      allPendingMatches: pendingMatches.map(m => ({
        id: m.id,
        date: m.match_date,
        team1_players: m.team1?.players,
        team2_players: m.team2?.players,
        team1_approved: m.team1?.players?.filter(p => p.approval_status === 'approved'),
        team2_approved: m.team2?.players?.filter(p => p.approval_status === 'approved'),
        team1_pending: m.team1?.players?.filter(p => p.approval_status === 'pending'),
        team2_pending: m.team2?.players?.filter(p => p.approval_status === 'pending')
      })),
      filteredOtherPendingMatches: otherPendingMatches.map(m => ({
        id: m.id,
        date: m.match_date,
        team1_players: m.team1?.players,
        team2_players: m.team2?.players,
        team1_approved: m.team1?.players?.filter(p => p.approval_status === 'approved'),
        team2_approved: m.team2?.players?.filter(p => p.approval_status === 'approved'),
        team1_pending: m.team1?.players?.filter(p => p.approval_status === 'pending'),
        team2_pending: m.team2?.players?.filter(p => p.approval_status === 'pending')
      }))
    });
  }, [pendingMatches, user, tab, otherPendingMatches]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
    fetchUsers();
  }, [user, navigate]);

  // Agregar efecto para reiniciar el scroll
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!user) {
    return null;
  }

  const renderContent = () => {
    if (tab === 0) {
      // Tab "Aprobadas" - Solo partidas aprobadas
      return (
        <RecentMatchesSection
          matches={matches}
          usersMap={usersMap}
          userId={user.id}
          isLoading={isLoadingMatches}
          showOnlyApproved={true}
          hideTitle={true}
        />
      );
    } else if (tab === 1) {
      // Tab "Pendientes" - Dividida en dos secciones
      return (
        <Stack spacing={2}>
          {/* Sección: Esperando mi aprobación */}
          <SectionTitle title="Esperando mi aprobación">
            {isLoadingPendingMatches ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={40} />
              </Box>
            ) : matchesNeedingUserApproval.length > 0 ? (
              <Stack spacing={1.5}>
                {matchesNeedingUserApproval.map((match) => (
                  <PendingMatchCard 
                    key={match.id} 
                    match={match} 
                    usersMap={usersMap}
                    onClick={() => navigate(`/match/${match.id}`)}
                    showApprovalButtons={true}
                    onApprove={handleApprove}
                    onReject={handleReject}
                  />
                ))}
              </Stack>
            ) : (
              <Box sx={{ 
                py: 2,
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                gap: 1
              }}>
                <GameIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                <Typography color="text.secondary">
                  No hay partidas esperando tu aprobación
                </Typography>
              </Box>
            )}
          </SectionTitle>

          {/* Sección: Esperando aprobación de otros */}
          <SectionTitle title="Esperando aprobación de otros">
            {isLoadingPendingMatches ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={40} />
              </Box>
            ) : otherPendingMatches.length > 0 ? (
              <Stack spacing={1.5}>
                {otherPendingMatches.map((match) => (
                  <PendingMatchCard 
                    key={match.id} 
                    match={match} 
                    usersMap={usersMap}
                    onClick={() => navigate(`/match/${match.id}`)}
                    showApprovalButtons={false}
                    onApprove={null}
                    onReject={null}
                  />
                ))}
              </Stack>
            ) : (
              <Box sx={{ 
                py: 2,
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                gap: 1
              }}>
                <GameIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                <Typography color="text.secondary">
                  No hay partidas esperando aprobación de otros jugadores
                </Typography>
              </Box>
            )}
          </SectionTitle>
        </Stack>
      );
    } else {
      // Tab "Canceladas/Rechazadas"
      return (
        <Box>
          {isLoadingMatches ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={40} />
            </Box>
          ) : cancelledMatches.length > 0 ? (
            <Stack spacing={1.5}>
              {cancelledMatches.map((match) => (
                <CancelMatchCard 
                  key={match.id} 
                  match={match} 
                  usersMap={usersMap}
                  onClick={() => navigate(`/match/${match.id}`)}
                />
              ))}
            </Stack>
          ) : (
            <Box sx={{ 
              py: 2,
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              gap: 1
            }}>
              <GameIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
              <Typography color="text.secondary">
                No hay partidas canceladas o rechazadas
              </Typography>
            </Box>
          )}
        </Box>
      );
    }
  };

  return (
    <Container 
      maxWidth="sm" 
      sx={{ 
        py: 3,
        px: { xs: 0.5, sm: 1 },
        width: '88%',
        maxWidth: '500px !important',
        ml: { xs: 0.7, sm: 0.7 }
      }}
    >
      <Stack spacing={1} sx={{ width: '100%', bgcolor: 'transparent' }}>
        <PageTitle title="Mis Partidas" />
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden'
          }}
        >
          <Tabs 
            value={tab} 
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                minHeight: '48px',
                fontSize: '0.95rem'
              }
            }}
          >
            <Tab 
              label="Aprobadas"
              icon={<ApprovedIcon sx={{ fontSize: '1.2rem' }} />}
              iconPosition="start"
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>Pendientes</Typography>
                  {otherPendingMatches.length > 0 && (
                    <Box
                      sx={{
                        bgcolor: 'warning.main',
                        color: 'warning.contrastText',
                        borderRadius: '12px',
                        px: 1,
                        minWidth: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem'
                      }}
                    >
                      {otherPendingMatches.length}
                    </Box>
                  )}
                </Box>
              }
              icon={<WatchLaterIcon sx={{ fontSize: '1.2rem' }} />}
              iconPosition="start"
            />
            <Tab 
              label="Canceladas"
              icon={<BlockIcon sx={{ fontSize: '1.2rem' }} />}
              iconPosition="start"
            />
          </Tabs>
          <Box sx={{ p: 2 }}>
            {renderContent()}
          </Box>
        </Paper>
      </Stack>
    </Container>
  );
}

export default MyMatchesPage; 